import { Bubble, ListItem } from '@chatui/core';
import { trpc } from '@tencent/tpro_rmonitor_ai_server';
import { Collapse } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { aiCopilotServer } from '@/service/ai-copilot';
import { WithTypeCast } from '@raft/utils';
import DownSelected from './icons/down-selected.react.svg?react';
import DownIcon from './icons/down.react.svg?react';
import ReGenIcon from './icons/regen.react.svg?react';
import UpSelectedIcon from './icons/up-selected.react.svg?react';
import UpIcon from './icons/up.react.svg?react';
import style from './index.module.scss';

const { Panel } = Collapse;

export type BuglyCopilotMessage = {
  content?: string;
  queryId: string;
  docList?: any;
  thinkingContent?: string;
};

type IMessage = WithTypeCast<trpc.rmonitor.ai_server.IMessage>;

enum LikeState {
  Unknown = 0,
  Like = 1,
  UnLike = 2
}

/**
 * Loading消息
 * @returns
 */
export const LoadingMessage = () => (
    <div className={style.typing} aria-busy="true">
      <div className={style.typingDot}></div>
      <div className={style.typingDot}></div>
      <div className={style.typingDot}></div>
    </div>
);

/**
 * 初始化推荐消息
 */
export const RecommandMessage = (props: {
  prompts: string[];
  onClickPrompt?: (prompt: string) => void;
}) => {
  const { prompts  = [], onClickPrompt } = props;


  return (
    <div>
      <span style={{ display: 'inline-block', fontSize: '14px', fontWeight: 500, lineHeight: '22px', paddingBottom: '8px'   }}>
        Hi! 我是Bugly Copilot AI助手
      </span>
      <br />
      <span style={{ display: 'inline-block', fontSize: '12px', fontWeight: 400, lineHeight: '18px', paddingBottom: '8px', color: '#7B889C'   }}>可以向我提问哦，我可以解答你的疑惑！试试以下问题：</span>
      {prompts?.map((prompt: string) => <div>
        <ListItem content={prompt} className={style.initList} onClick={() => onClickPrompt?.(prompt)} />
        </div>)}
    </div>
  );
};

/**
 * 回答文本
 * @param _props
 * @returns
 */
export const ResponseTextMessage = (_props: {
  question: string;
  message: BuglyCopilotMessage,
  onReGen?: (messageId: string, question: string, message: BuglyCopilotMessage) => void;
  messageId: string;
  // 消息是否已结束
  finished?: boolean;
  // 全局控制是否显示重新生成
  enableRegen?: boolean;
  showLike?: boolean
}) => {
  const { message, onReGen, messageId, question, finished = false, enableRegen, showLike = false } = _props;
  // 消息的点赞点踩状态，unknown时候两个都是灰
  const [likeState, setLikeState] = useState<LikeState>(LikeState.Unknown);
  // 控制思考过程的展开/折叠状态
  const [thinkingActiveKey, setThinkingActiveKey] = useState<string[]>(['thinking']);
  // 重新生成消息
  const content = message.content ?? '';

  // 当content有值后，自动折叠thinking
  React.useEffect(() => {
    if (content) {
      setThinkingActiveKey([]);
    } else {
      setThinkingActiveKey(['thinking']);
    }
  }, [content]);

  // 点赞
  const doLike = async () => {
    const nextState = (likeState === LikeState.Like ? LikeState.Unknown : LikeState.Like) as number;
    await aiCopilotServer.feedback({
      queryId: message.queryId,
      rating: nextState,
    });
    setLikeState(nextState);
  };

  // 点踩
  const doUnLike = async () => {
    const nextState = (likeState === LikeState.UnLike ? LikeState.Unknown : LikeState.UnLike) as number;
    await aiCopilotServer.feedback({
      queryId: message.queryId,
      rating: nextState,
    });
    setLikeState(nextState);
  };

  return (
      <Fragment>
        <Bubble >
        {/* 思考过程 - 可折叠 */}
        {message.thinkingContent && (
          <Collapse
            activeKey={thinkingActiveKey}
            onChange={(keys) => setThinkingActiveKey(keys as string[])}
            ghost
            style={{ marginBottom: content ? '12px' : 0 }}
          >
            <Panel
              key="thinking"
              header={<span style={{ fontSize: '12px', color: '#7B889C' }}>💭 思考过程</span>}
              style={{
                padding: 0,
              }}
              className="thinking-collapse-panel"
            >
              <ReactMarkdown>{message.thinkingContent}</ReactMarkdown>
            </Panel>
          </Collapse>
        )}
        {/* 正式回答内容 */}
        {content && <ReactMarkdown>{ `${content}\n` }</ReactMarkdown>}
        {/* 渲染引用文档 */}
        {!isEmpty(message.docList) && (
          <Fragment>
            <div className={style.refTitle}>引用如下资料：</div>
            <br />
            {message.docList.map((item: any) => (
            <div key={item.url}>
              <ListItem
                content={
                  <span style={{ color: '#1E304A' }}>
                    {item.title}
                    <br />
                  </span>
                }
                className={style.initList}
                onClick={() => window.open(item.url!, '_blank')}
              />
            </div>
            ))}
          </Fragment>
        )}
        {/* 消息结束才渲染消息的操作按钮 */}
        </Bubble>
        {(finished || showLike) && <div style={{ paddingTop: '8px', height: '18px', position: 'relative' }}>
          {/* 全局状态允许显示+是最后一条才显示 */}
          {finished && enableRegen && <span
            className="reGenIcon"
            style={{ cursor: 'pointer' }}
            onClick={() => onReGen?.(messageId, question, message)}
          >
            <ReGenIcon />
          </span>}
          <div style={{ position: 'absolute', right: 0, display: 'flex', gap: 8, top: 8 }}>
            <span
              onClick={doLike}
              style={{  cursor: 'pointer' }}
            >
              {likeState === LikeState.Like ? <UpSelectedIcon /> : <UpIcon/>}
            </span>
            <span
              onClick={doUnLike}
              style={{ cursor: 'pointer' }}
            >
              {likeState === LikeState.UnLike ? <DownSelected /> : <DownIcon/>}
            </span>
          </div>
        </div>}
      </Fragment>
  );
};


export const TimestampMessage = () => <>{moment().format('YYYY-MM-DD HH:mm:ss')}</>

;

export const HistoryRspTextMessage = (props: IMessage) => (
  <>
    <ReactMarkdown>{props.content as string}</ReactMarkdown>
    {props.docs?.length !== 0 ? (
      <div>
        <span className={style.refTitle}>引用如下资料：</span>
        <br />
        {props.docs?.map(doc => (
          <div key={doc.url}>
            <ListItem
              content={
                <span style={{ color: '#1E304A' }}>
                  {doc.title}
                  <br />
                </span>
              }
              className={style.initList}
              onClick={() => window.open(doc.url!, '_blank')}
            />
          </div>
        ))}
      </div>
    ) : null}
  </>
);
